import { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { StorageManager } from '../utils/storageManager';
import useYearGuessGame from './useYearGuessGame';
import useRevealDigits from './useRevealDigits';
import useImageFetching from './useImageFetching';
import useGameStats from './useGameStats';
import useProgressPersistence from './useProgressPersistence';
import {
  ChallengeSummaryResult,
  DistributionStats,
  GuessHistoryItem,
  WikimediaImage,
} from '../types/types';
import {
  MIN_YEAR,
  MAX_YEAR,
  API_BASE_URL,
  DAILY_API_URL,
  ANIMATION,
} from '../constants';
import {
  calculatePercentileFromCurve,
  scrollToTop,
} from '../utils/challengeUtils';

/**
 * Main Daily Challenge Controller
 * 
 * This is the central "brain" that orchestrates the entire daily challenge experience.
 * It coordinates multiple specialized hooks and manages the complex state flow between
 * different challenge states (new game, in-progress, completed).
 * 
 * KEY RESPONSIBILITIES:
 * 1. State Restoration: Determines what state to show when a user visits a challenge
 * 2. Progress Persistence: Saves game state as users play so they can resume later
 * 3. Results Management: Handles the transition from gameplay to results viewing
 * 4. Data Orchestration: Coordinates between game logic, image fetching, and statistics
 * 
 * STATE FLOW LOGIC:
 * The hook follows a specific priority order when determining what to show:
 * 1. Check if completed results exist -> Show results screen
 * 2. Check if active game state exists -> Restore in-progress game
 * 3. Otherwise -> Initialize fresh new game
 * 
 * This priority ensures users never lose completed games or in-progress state.
 */

interface DailyChallengeViewProps {
  onShowInstructions: () => void;
  onShowCalendar: () => void;
  selectedDate?: string;
  isCurrentDaily?: boolean;
}

export const useDailyChallengeController = ({
  onShowInstructions,
  onShowCalendar,
  selectedDate,
  isCurrentDaily = false,
}: DailyChallengeViewProps) => {
  const startEventSentRef = useRef(false);

  const persistence = useProgressPersistence(selectedDate);
  
  // Check if this challenge already has completed results
  // If so, we'll skip loading active game state since results take priority
  const initialGameStateFromStorage = useMemo(() => {
    if (!selectedDate) return null;
    const resultsData = StorageManager.loadResultsData(selectedDate);
    if (resultsData) return null; // Results exist, don't load active state
    return persistence.loadGameState();
  }, [selectedDate, persistence]);

  // Convert stored game state back into the format expected by useYearGuessGame
  // This handles the restoration of user input, revealed digits, and progress
  const initialYearGuessGameState = useMemo(() => {
    if (initialGameStateFromStorage) {
      // Recalculate total score from individual round scores to ensure consistency
      const recalculatedScore = (
        initialGameStateFromStorage.pointsPerRound || []
      ).reduce((sum: number, pts: number) => sum + pts, 0);
      return {
        hasGuessed: initialGameStateFromStorage.hasGuessed === true,
        yearInput: initialGameStateFromStorage.yearInput || ['', '', '', ''],
        revealedDigits: initialGameStateFromStorage.revealedDigits || [
          false,
          false,
          false,
          false,
        ],
        score: recalculatedScore,
        guessHistory: initialGameStateFromStorage.guessHistory || [],
      };
    }
    return {};
  }, [initialGameStateFromStorage]);

  const [showAllTimeStats, setShowAllTimeStats] = useState(false);
  const [showConfettiBoom, setShowConfettiBoom] = useState(false);
  const [completedChallenge, setCompletedChallenge] = useState(false);
  const [restorationLoading, setRestorationLoading] = useState(true);
  const [challengeResultData, setChallengeResultData] =
    useState<ChallengeSummaryResult | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const yearGuessGame = useYearGuessGame({
    minYear: MIN_YEAR,
    maxYear: MAX_YEAR,
    initialState: initialYearGuessGameState,
  });
  const revealDigits = useRevealDigits();
  const imageFetching = useImageFetching({ mode: 'daily', selectedDate });
  const gameStats = useGameStats({ mode: 'daily', selectedDate });
  const [isTodayChallenge, setIsTodayChallenge] = useState(isCurrentDaily);

  const didRestore = useRef(false);

  useEffect(() => {
    scrollToTop();
  }, []);

  // Handle mobile viewport height issues
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    window.scrollTo(0, 0);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  useEffect(() => {
    if (isCurrentDaily !== undefined) {
      setIsTodayChallenge(isCurrentDaily);
    } else {
      const todayET = StorageManager.getTodayDateET();
      const currentChallengeDate = selectedDate || todayET;
      setIsTodayChallenge(currentChallengeDate === todayET);
    }
  }, [selectedDate, isCurrentDaily]);

  // Reset state when switching between different challenge dates
  useEffect(() => {
    if (selectedDate === undefined) return;
    didRestore.current = false;
    setCompletedChallenge(false);
    setRestorationLoading(true);
    setChallengeResultData(null);
    scrollToTop();
  }, [selectedDate]);

  /**
   * MAIN STATE RESTORATION LOGIC
   * 
   * This is the most complex part of the controller. It determines what state to show
   * based on the priority order: Results > In-Progress Game > New Game
   * 
   * FLOW BREAKDOWN:
   * 1. Wait for challenge data to load before making decisions
   * 2. Check for completed results first (highest priority)
   *    - If found: Show results screen and fetch additional stats from API
   * 3. Check for saved in-progress game state 
   *    - If found: Restore all the hooks to their previous state
   * 4. Otherwise: Initialize a fresh new game
   * 
   * The complexity here comes from needing to coordinate multiple hooks and ensure
   * they all get restored to a consistent state that matches what was saved.
   */
  useEffect(() => {
    const currentChallengeDate = selectedDate || StorageManager.getTodayDate();

    // Prevent multiple restoration attempts
    if (didRestore.current) return;

    // Don't start restoration until we have challenge data loaded
    const resultsData = StorageManager.loadResultsData(currentChallengeDate);
    if (!resultsData && !imageFetching.challenge) {
      setRestorationLoading(true);
      return;
    }

    setRestorationLoading(true);

    // CASE 1: Completed results exist - show results screen
    if (resultsData && typeof resultsData.score === 'number') {
      const summaryData = resultsData as ChallengeSummaryResult;
      
      // Reset all hooks to clean state since we're viewing results, not playing
      yearGuessGame.resetRound();
      revealDigits.resetAllRevealed();
      gameStats.resetStats();
      setChallengeResultData(summaryData);
      setCompletedChallenge(true);
      setHasUserInteracted(false);
      setRestorationLoading(true);

      // Fetch additional stats and image URLs from API to enrich the results display
      const fetchFullResultsData = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/images/daily-challenge/stats`,
            {
              params: { date: currentChallengeDate },
            }
          );
          if (response.data) {
            const apiDistributionStats = response.data.stats;
            const apiImageUrls = response.data.imageUrls;
            if (!apiDistributionStats || !Array.isArray(apiImageUrls)) {
              throw new Error('Incomplete data received from stats API');
            }
            // Calculate where this user's score ranks compared to all other players
            const calculatedPercentile = calculatePercentileFromCurve(
              summaryData.score,
              apiDistributionStats?.processedDistribution?.curvePoints ?? []
            );
            
            // Combine local results data with fresh API data for complete results
            const completeResultsData: ChallengeSummaryResult = {
              ...summaryData,
              imageUrls: apiImageUrls,
              distributionStats: {
                averageScore: apiDistributionStats?.averageScore ?? 0,
                completions: apiDistributionStats?.completions ?? 0,
                distributions: apiDistributionStats?.distributions ?? [],
                processedDistribution:
                  apiDistributionStats?.processedDistribution,
                percentileRank: calculatedPercentile,
              },
              challengeStats: summaryData.challengeStats || {
                score: 0,
                correctGuesses: 0,
                totalRounds: 0,
                averageYearsOff: 0,
              },
              isPastChallenge: !isTodayChallenge,
              usedHints: summaryData.usedHints === true,
            };
            setChallengeResultData(completeResultsData);
          }
        } finally {
          didRestore.current = true;
          setRestorationLoading(false);
        }
      };
      fetchFullResultsData();
    } 
    // CASE 2: No completed results, check for in-progress game state
    else {
      const loadedActiveState = initialGameStateFromStorage;
      if (loadedActiveState) {
        // Attempt to restore all hooks to their previous state
        try {
          // Validate that we have the essential data needed for restoration
          if (
            typeof loadedActiveState.currentRound !== 'number' ||
            !Array.isArray(loadedActiveState.pointsPerRound)
          ) {
            throw new Error(
              'Initial active state is missing essential fields (currentRound or pointsPerRound).'
            );
          }
          
          // Restore digit reveal state (which digits were revealed across all rounds)
          if (loadedActiveState.challengeRevealedDigits) {
            revealDigits.updateChallengeRevealedDigits(
              loadedActiveState.challengeRevealedDigits.map(
                (v: any) => v === true
              )
            );
          } else {
            revealDigits.resetAllRevealed();
          }
          revealDigits.updateRevealUsedThisRound(
            loadedActiveState.revealUsedThisRound === true
          );
          
          // Restore game statistics and progress
          gameStats.updateAllGuesses(loadedActiveState.allGuesses || []);
          gameStats.updatePointsPerRound(
            loadedActiveState.pointsPerRound || []
          );
          gameStats.setPointsGainedDirectly(
            loadedActiveState.pointsGained ?? null
          );
          
          // Restore which round the user was on
          const roundToRestore = loadedActiveState.currentRound ?? 0;
          if (
            imageFetching.challenge?.images &&
            roundToRestore < imageFetching.challenge.images.length
          ) {
            imageFetching.setCurrentRound(roundToRestore);
          }
          
          // Set state to indicate we're in an active game, not viewing results
          setCompletedChallenge(false);
          setChallengeResultData(null);
          setHasUserInteracted(true);
        } catch (restoreError: any) {
          // If restoration fails, fall back to a fresh game state
          yearGuessGame.resetRound();
          revealDigits.resetAllRevealed();
          gameStats.resetStats();
          setCompletedChallenge(false);
          setChallengeResultData(null);
          setHasUserInteracted(false);
          imageFetching.setCurrentRound(0);
        }
      } 
      // CASE 3: No saved state, initialize fresh game
      else {
        yearGuessGame.resetRound();
        revealDigits.resetAllRevealed();
        gameStats.resetStats();
        setCompletedChallenge(false);
        setChallengeResultData(null);
        setHasUserInteracted(false);
        imageFetching.setCurrentRound(0);
      }
      didRestore.current = true;
      setRestorationLoading(false);

      // Send analytics event for new challenge starts (but only once)
      if (
        imageFetching.challenge &&
        !completedChallenge &&
        !startEventSentRef.current
      ) {
        if (typeof window.gtag === 'function') {
          const todayET = StorageManager.getTodayDateET();
          const dateParam = selectedDate || todayET;
          const isPast = dateParam !== todayET;
          window.gtag('event', 'start_challenge', {
            challenge_type: 'daily',
            challenge_date: dateParam,
            is_past_challenge: isPast,
          });
          startEventSentRef.current = true;
        }
      }
    }
  }, [
    selectedDate,
    imageFetching.challenge,
    initialGameStateFromStorage,
    completedChallenge,
    isTodayChallenge,
  ]);

  /**
   * PROGRESS PERSISTENCE
   * 
   * Continuously saves the current game state to localStorage so users can resume
   * their progress if they close the tab or navigate away. Only saves when:
   * 1. Not in a loading state
   * 2. Have an active challenge loaded
   * 3. Not viewing completed results
   * 4. User has actually interacted with the game
   * 
   * The saved state captures everything needed to restore the exact game state:
   * - Current round and user input
   * - Which digits have been revealed
   * - All previous guesses and scores
   * - Points gained feedback state
   */
  useEffect(() => {
    if (!restorationLoading && imageFetching.challenge && !completedChallenge) {
      const stateToSave = {
        currentRound: imageFetching.currentRound,
        hasGuessed: yearGuessGame.state.hasGuessed,
        yearInput: yearGuessGame.state.yearInput,
        revealedDigits: yearGuessGame.state.revealedDigits,
        challengeRevealedDigits: revealDigits.challengeRevealedDigits,
        revealUsedThisRound: revealDigits.revealUsedThisRound,
        allGuesses: gameStats.allGuesses,
        pointsPerRound: gameStats.pointsPerRound,
        pointsGained: gameStats.pointsGained,
        viewingResults: false,
      };
      
      // Only save if user has actually started playing (prevents saving blank state)
      if (hasUserInteracted) {
        const existingState = persistence.loadGameState() || {};
        persistence.saveGameState({
          ...existingState,
          ...stateToSave,
        });
      }
    }
  }, [
    restorationLoading,
    completedChallenge,
    imageFetching,
    yearGuessGame,
    revealDigits,
    gameStats,
    selectedDate,
    hasUserInteracted,
    persistence,
  ]);

  const handleGuess = () => {
    if (!imageFetching.currentImage) return;
    const result = yearGuessGame.makeGuess(imageFetching.currentImage.year);
    if (!result) return;
    
    // Record this guess in our game statistics
    gameStats.recordGuess(
      imageFetching.currentImage.year,
      result.guessedYear,
      result.points
    );
    
    // Send analytics for this round completion
    if (typeof window.gtag === 'function') {
      const dateParam = selectedDate || StorageManager.getTodayDateET();
      window.gtag('event', 'round_complete', {
        challenge_type: 'daily',
        challenge_date: dateParam,
        round_number: imageFetching.currentRound + 1,
        score_earned: result.points,
        years_off: Math.abs(
          result.guessedYear - imageFetching.currentImage.year
        ),
        is_correct: result.isCorrect,
      });
    }
    
    setHasUserInteracted(true);
    
    // Show celebration animation for perfect guesses
    if (result.isCorrect) {
      setShowConfettiBoom(true);
      setTimeout(() => setShowConfettiBoom(false), ANIMATION.CONFETTI_DURATION);
    }
    
    const historyToSave: GuessHistoryItem[] = [
      { year: result.guessedYear, correct: result.isCorrect },
    ];
    persistence.saveGameState({
      currentRound: imageFetching.currentRound,
      hasGuessed: true,
      yearInput: yearGuessGame.state.yearInput,
      revealedDigits: yearGuessGame.state.revealedDigits,
      challengeRevealedDigits: revealDigits.challengeRevealedDigits,
      revealUsedThisRound: revealDigits.revealUsedThisRound,
      allGuesses: gameStats.allGuesses,
      pointsPerRound: gameStats.pointsPerRound,
      pointsGained: result.points,
      guessHistory: historyToSave,
      viewingResults: false,
    });
  };

  const handleHint = () => {
    if (yearGuessGame.state.hasGuessed || !imageFetching.currentImage) return;
    if (revealDigits.revealUsedThisRound) return;
    if (revealDigits.allDigitsRevealed()) return;
    yearGuessGame.toggleDigitSelection();
  };

  const handleDigitReveal = (index: number) => {
    if (
      !imageFetching.currentImage ||
      yearGuessGame.state.hasGuessed ||
      !yearGuessGame.state.selectingDigit
    )
      return;
    if (revealDigits.challengeRevealedDigits[index]) return;
    const currentYear = imageFetching.currentImage.year
      .toString()
      .padStart(4, '0');
    const updatedChallengeRevealedDigits = [
      ...revealDigits.challengeRevealedDigits,
    ];
    updatedChallengeRevealedDigits[index] = true;
    revealDigits.updateChallengeRevealedDigits(updatedChallengeRevealedDigits);
    revealDigits.updateRevealUsedThisRound(true);
    const newYearInput = ['', '', '', ''];
    const newRevealedDigits = [false, false, false, false];
    newRevealedDigits[index] = true;
    newYearInput[index] = currentYear[index];
    setHasUserInteracted(true);
    yearGuessGame.setState((prev) => ({
      ...prev,
      yearInput: newYearInput,
      revealedDigits: newRevealedDigits,
      selectingDigit: false,
    }));
    persistence.saveGameState({
      currentRound: imageFetching.currentRound,
      hasGuessed: yearGuessGame.state.hasGuessed,
      yearInput: newYearInput,
      revealedDigits: newRevealedDigits,
      challengeRevealedDigits: updatedChallengeRevealedDigits,
      revealUsedThisRound: true,
      allGuesses: gameStats.allGuesses,
      pointsPerRound: gameStats.pointsPerRound,
      pointsGained: gameStats.pointsGained,
      viewingResults: false,
    });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'use_hint', {
        round_number: imageFetching.currentRound + 1,
        digit_index: index,
        challenge_type: 'daily',
      });
    }
  };

  const handleNextRound = async () => {
    setShowConfettiBoom(false);
    if (!imageFetching.challenge) return;

    const currentChallengeDate = selectedDate || StorageManager.getTodayDate();
    const isLastRound = imageFetching.isLastRound();

    if (isLastRound) {
      const finalStats = gameStats.calculateFinalStats(
        imageFetching.totalRounds,
        yearGuessGame.state.score
      );
      const hintsUsedInChallenge = revealDigits.challengeRevealedDigits.some(
        (revealed) => revealed === true
      );

      if (typeof window.gtag === 'function') {
        const todayET = StorageManager.getTodayDateET();
        const dateParam = selectedDate || todayET;
        const isPast = dateParam !== todayET;
        window.gtag('event', 'challenge_complete', {
          challenge_type: 'daily',
          challenge_date: dateParam,
          final_score: finalStats.score,
          average_years_off: finalStats.averageYearsOff,
          correct_guesses: finalStats.correctGuesses,
          total_rounds: finalStats.totalRounds,
          is_past_challenge: isPast,
        });
      }

      StorageManager.saveGameResult(
        currentChallengeDate,
        {
          score: finalStats.score,
          averageYearsOff: finalStats.averageYearsOff,
        },
        {
          correctGuesses: finalStats.correctGuesses,
        }
      );

      const roundDataSummary = gameStats.allGuesses.map((guess, index) => ({
        roundNumber: index + 1,
        guessedYear: guess.guessedYear,
        actualYear: guess.actualYear,
        score: gameStats.pointsPerRound[index] || 0,
      }));

      const finalImageUrls = imageFetching.challenge.images
        .slice(0, gameStats.allGuesses.length)
        .map((img: WikimediaImage) => img.url);

      const summaryResultData: ChallengeSummaryResult = {
        score: finalStats.score,
        averageYearsOff: finalStats.averageYearsOff,
        roundData: roundDataSummary,
        imageUrls: finalImageUrls,
        totalRounds: imageFetching.totalRounds,
        correctGuesses: finalStats.correctGuesses,
        challengeStats: finalStats,
        distributionStats: {
          distributions: gameStats.distributionStats.distributions || [],
          processedDistribution:
            gameStats.distributionStats.processedDistribution,
          averageScore: gameStats.distributionStats.averageScore || 0,
          completions: gameStats.distributionStats.completions || 0,
          percentileRank: gameStats.distributionStats.percentileRank,
        },
        dateCompleted: currentChallengeDate,
        isPastChallenge: !isTodayChallenge,
        usedHints: hintsUsedInChallenge,
      };

      const stateForResultsPersistence = {
        viewingResults: true,
        challengeResultData: summaryResultData,
        currentRound: imageFetching.currentRound,
        score: finalStats.score,
        hasGuessed: true,
        yearInput: ['', '', '', ''],
        revealedDigits: [false, false, false, false],
        challengeRevealedDigits: revealDigits.challengeRevealedDigits,
        revealUsedThisRound: false,
        guessHistory: [],
        allGuesses: [],
        pointsPerRound: [],
        pointsGained: null,
      };
      persistence.saveGameState(stateForResultsPersistence);

      if (!isTodayChallenge) {
        localStorage.removeItem(
          StorageManager.KEYS.ACTIVE_GAME + currentChallengeDate
        );
        localStorage.removeItem(
          `yearGuess_revealedDigitsBackup_${currentChallengeDate}`
        );
        localStorage.removeItem(`yearGuess_lastRound_${currentChallengeDate}`);
      }

      setChallengeResultData(summaryResultData);
      setCompletedChallenge(true);

      const roundGuessesPayload = gameStats.allGuesses.map((guess, index) => ({
        roundIndex: index,
        guessedYear: guess.guessedYear,
      }));

      imageFetching
        .submitScore(finalStats.score, roundGuessesPayload)
        .then((stats) => {
          if (stats) {
            const calculatedPercentile = calculatePercentileFromCurve(
              finalStats.score,
              stats.processedDistribution?.curvePoints ?? []
            );
            const apiDistributionStats: DistributionStats = {
              averageScore: stats.averageScore ?? 0,
              completions: stats.completions ?? 0,
              distributions: stats.distributions ?? [],
              processedDistribution: stats.processedDistribution,
              percentileRank: calculatedPercentile,
            };
            setChallengeResultData((prev) => {
              if (!prev) return summaryResultData;
              return {
                ...prev,
                distributionStats: {
                  ...prev.distributionStats,
                  ...apiDistributionStats,
                },
              };
            });
          }
        });

      if (!isTodayChallenge) {
        const fetchPastStats = async () => {
          try {
            const response = await axios.get(
              `${DAILY_API_URL}/daily-challenge/stats`,
              {
                params: { date: currentChallengeDate },
              }
            );
            if (response.data?.stats) {
              const stats = response.data.stats as DistributionStats;
              setChallengeResultData(
                (prevData: ChallengeSummaryResult | null) => {
                  const baseData = summaryResultData;
                  return {
                    ...baseData,
                    distributionStats: {
                      ...baseData.distributionStats,
                      ...stats,
                    },
                  };
                }
              );
            }
          } catch (error) {}
        };
        fetchPastStats();
      }

      const {
        imageUrls: _excludedImageUrls,
        distributionStats: _excludedDistStats,
        ...resultsToSave
      } = summaryResultData;
      StorageManager.saveResultsData(
        currentChallengeDate,
        resultsToSave as Omit<
          ChallengeSummaryResult,
          'imageUrls' | 'distributionStats'
        >
      );
    } else {
      imageFetching.nextRound();
      yearGuessGame.resetRound();
      revealDigits.resetRevealedForRound();
      gameStats.resetPointsGained();
    }
  };

  return {
    state: {
      completedChallenge,
      challengeResultData,
      restorationLoading,
      isTodayChallenge,
      showAllTimeStats,
      showConfettiBoom,
      game: yearGuessGame.state,
      image: imageFetching.currentImage,
      imageLoading: imageFetching.loading,
      imageError: imageFetching.error,
      challenge: imageFetching.challenge,
      currentRound: imageFetching.currentRound,
      totalRounds: imageFetching.totalRounds,
      isLastRound: imageFetching.isLastRound(),
      revealedDigits: revealDigits.challengeRevealedDigits,
      revealUsedThisRound: revealDigits.revealUsedThisRound,
      allDigitsRevealed: revealDigits.allDigitsRevealed(),
      pointsGained: gameStats.pointsGained,
      score: yearGuessGame.state.score,
      correctGuesses: yearGuessGame.state.guessHistory.filter(
        (gh) => gh.correct
      ).length,
    },
    handlers: {
      handleGuess,
      handleHint,
      handleDigitReveal,
      handleNextRound,
      handleSliderChange: yearGuessGame.handleSliderChange,
      handleTimelineClick: yearGuessGame.handleTimelineClick,
      handleDigitInput: yearGuessGame.handleDigitInput,
      handleKeyDown: yearGuessGame.handleKeyDown,
      handlePaste: yearGuessGame.handlePaste,
      setShowAllTimeStats,
      onShowInstructions,
      onShowCalendar,
    },
  };
};
